import { ChevronLeftIcon } from "lucide-react";

import type { CmmdBoardTask, CmmdMember, CmmdProjectAgentMember } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { resolveTaskThreadHistory } from "~/lib/taskThreadLink";
import { ProjectTaskDetailEditor } from "./ProjectTaskDetailEditor";
import type { ProjectTaskBoard } from "./useProjectTaskBoard";
import type { ProjectTaskWorkbenchDataProps } from "./projectTaskWorkbenchTypes";

export interface ChatTaskDetailPaneProps {
  readonly task: CmmdBoardTask;
  readonly projectTitle: string;
  readonly projectIcon: string | null;
  readonly projectIconUrl: string | null;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading: boolean;
  readonly agentMembersError: string | null;
  /** Mutation surface, shared with the project board so writes behave alike. */
  readonly board: ProjectTaskBoard;
  readonly workbenchData: ProjectTaskWorkbenchDataProps;
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly onStartWork: (task: CmmdBoardTask) => Promise<void> | void;
  /** Navigate to a Forge thread from the card's task thread history. */
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
  readonly onOpenTask: (taskId: number) => void;
  /** Return to the project board, or `null` when this thread has no board view. */
  readonly onBack: (() => void) | null;
}

/**
 * The chat sidebar's task detail surface.
 *
 * This renders the same {@link ProjectTaskDetailEditor} the project surface
 * modal renders, rather than a sidebar-shaped copy of it. The panel previously
 * showed a summary strip that drifted out of date as the task board gained
 * features; hosting the one component is what stops that from recurring.
 *
 * Writes are wired to the shared board handlers, so an edit here and an edit in
 * the project modal take the same path — including optimistic update, rollback
 * and the error surfaced back through `board.mutationError`.
 */
export function ChatTaskDetailPane(props: ChatTaskDetailPaneProps) {
  const { board, task } = props;
  return (
    <div className="flex h-full min-h-0 flex-col">
      {props.onBack !== null ? (
        <button
          type="button"
          onClick={props.onBack}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 border-b border-border px-3 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeftIcon className="size-3.5" />
          Back to board
        </button>
      ) : null}
      <div className="min-h-0 flex-1 overflow-hidden">
        <ProjectTaskDetailEditor
          key={task.id}
          task={task}
          projectTitle={props.projectTitle}
          projectIconUrl={props.projectIconUrl}
          projectIcon={props.projectIcon}
          allTasks={props.allTasks}
          members={props.members}
          agentMembers={props.agentMembers}
          agentMembersLoading={props.agentMembersLoading}
          agentMembersError={props.agentMembersError}
          pending={board.pendingTaskIds.has(task.id)}
          statusMutationDisabled={board.workflowMutationBlocked}
          creating={board.creating}
          mutationError={board.mutationError}
          executing={false}
          hasKnownConversation={false}
          workflow={board.workflow}
          threadHistory={resolveTaskThreadHistory(props.threadSummaries, task.id)}
          workbenchData={props.workbenchData}
          onUpdate={board.updateTask}
          onSetAssignment={board.setAssignment}
          onCreateTask={board.createTask}
          onSetStatus={board.setStatus}
          onStartWork={startWork}
          onOpenThread={props.onOpenThread}
          onOpenTask={props.onOpenTask}
          onArchive={() => board.archive(task.id)}
          onUnarchive={() => board.unarchive(task.id)}
        />
      </div>
    </div>
  );

  /**
   * The editor's contract resolves to "did the work start", but the launcher
   * only opens its dialog — the session starts later, from inside it. Report
   * `false` so the editor does not present work as begun before it is.
   */
  async function startWork(): Promise<boolean> {
    await props.onStartWork(task);
    return false;
  }
}
